import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { MediaPlayer } from "./MediaPlayer";
import { useSelector } from "react-redux";
import AnimatedWrap from "../../../../SharedComponents/DragDrop/animateMedia";

function PlaylistPreview({ 
    playlist, 
    pause_start_time, 
    startTime, 
    animConfig,
    displayDimensions = null,
    useResponsiveScale = false
}) {
    const { paused } = useSelector((state) => state.device);
    const mediaList = playlist?.playlist?.MAIN || [];

    // elapsed time across the WHOLE playlist (kept for deciding which item we're on)
    const [elapsedSeconds, setElapsedSeconds] = useState(() => {
        const now = paused ? moment(pause_start_time) : moment();
        return now.diff(moment(startTime), "seconds");
    });

    useEffect(() => {
        let id;
        if (!paused) {
            id = setInterval(() => {
                const now = moment();
                setElapsedSeconds(now.diff(moment(startTime), "seconds"));
            }, 1000);
        }
        return () => clearInterval(id);
    }, [paused, startTime]);

    useEffect(() => {
        if (paused && pause_start_time) {
            const now = moment(pause_start_time);
            setElapsedSeconds(now.diff(moment(startTime), "seconds"));
        }
    }, [paused, pause_start_time, startTime]);

    // durations and cumulative starts
    const durations = useMemo(
        () => mediaList.map(m => Number(m.custom_duration || m.default_duration || 10)),
        [mediaList]
    );

    const totalDuration = useMemo(
        () => durations.reduce((a, b) => a + b, 0),
        [durations]
    );

    const starts = useMemo(() => {
        const arr = [];
        let acc = 0;
        for (const d of durations) {
            arr.push(acc);
            acc += d;
        }
        return arr;
    }, [durations]);

    // derive which index we should be on, given elapsedSeconds
    const [currentIndex, setCurrentIndex] = useState(0);
    const [frozenOffset, setFrozenOffset] = useState(0); // only updates when item changes
    const prevIndexRef = useRef(-1);

    useEffect(() => {
        if (mediaList.length === 0 || totalDuration === 0) return;

        // support looping
        const loopTime = ((elapsedSeconds % totalDuration) + totalDuration) % totalDuration;

        // find index
        let idx = 0;
        for (let i = 0; i < starts.length; i++) {
            const start = starts[i];
            const end = start + durations[i];
            if (loopTime >= start && loopTime < end) { idx = i; break; }
        }

        // only when item changes, update index and freeze its start offset
        if (idx !== prevIndexRef.current) {
            prevIndexRef.current = idx;
            setCurrentIndex(idx);
            setFrozenOffset(loopTime - starts[idx]); // initial offset within this item
        }
        // otherwise do nothing (avoid prop churn)
    }, [elapsedSeconds, mediaList.length, totalDuration, starts, durations]);

    const currentMedia = mediaList[currentIndex] || null;
    if (!currentMedia) return null;

    // --- animation config (per-layer) ---
    const enabled = !!animConfig?.enabled;
    const type = animConfig?.type || "FADE";
    const direction = animConfig?.direction || "CENTER";
    const durationMs = (animConfig?.duration ? animConfig.duration * 1000 : 600) || 600;

    const loopCount = totalDuration > 0 ? Math.floor(elapsedSeconds / totalDuration) : 0;

    const mediaId =
        currentMedia.assigned_id ||
        currentMedia.media_id ||
        currentMedia.layout_id ||
        currentMedia.playlist_id ||
        "m";

    // key changes only when media (or loop) changes -> remounts player once per switch
    const itemKey = `${mediaId}-${loopCount}`;

    const node = (
        <MediaPlayer
            key={itemKey}
            data={currentMedia}
            // <-- IMPORTANT: pass a stable "start" that only changes on item switch
            elapsedSeconds={frozenOffset}
            customDuration={Number(currentMedia.custom_duration || currentMedia.default_duration || 10)}
            displayDimensions={displayDimensions}
            useResponsiveScale={useResponsiveScale}
        />
    );

    return (
        <div className="place-items-center w-full text-center h-full">
            {enabled ? (
                <AnimatedWrap
                    itemKey={itemKey}
                    type={type}
                    direction={direction}
                    durationMs={durationMs}
                    className="w-full h-full"
                >
                    {node}
                </AnimatedWrap>
            ) : (
                node
            )}
        </div>
    );
}

export default PlaylistPreview;
